import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import SidebarTechTree from "./SidebarTechTree";
import EliteMarkdownViewer from "./EliteMarkdownViewer";
import ThemeToggle from "./ThemeToggle";
import EliteSelectionModal from "./EliteSelectionModal";
import PremiumUpgradeModal from "./PremiumUpgradeModal";
import {
  BookOpen,
  ChevronDown,
  Search,
  Sparkles,
  ArrowLeft
} from "lucide-react";
import ChatBot from "./SkillGenBot/ChatBot";
import { useTheme } from "../contexts/ThemeContext";
import { useSubscription } from "../hooks/useSubscription";
import { useTenant } from "../Context/TenantContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";
// 27-Aug-2026: the same topic-sequence model EliteTechnologyMastery uses —
// "Topic X of Y", Previous / Next across sections, deep links (?topic= / ?file=).
import {
  buildTechnologyTree, buildSequences, locateFile, resolveNavigation, legacyIsLocked, isLegacyCollegeUser,
  topicSearch, parseTopicSearch, completedFiles,
} from "./elite/eliteTopicSequence";
import { TopicPosition, TopicNavigationBar, OnThisPageDropdown } from "./elite/EliteTopicNavigation";
import API_BASE_URL from "../Config/config";
import useEliteTechnologies from "../hooks/useEliteTechnologies";

function slugify(str) {
  return str.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

const EliteTechnologyDetail = () => {
  const { tech } = useParams();
  const [technologies, setTechnologies] = useState([]);
  const [selected, setSelected] = useState({ tech: tech, topic: null, subtopic: null });
  const [mapping, setMapping] = useState(null);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lockedFile, setLockedFile] = useState(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [headings, setHeadings] = useState([]);
  const [activeHeading, setActiveHeading] = useState("");

  const { theme } = useTheme();
  const { isPaid, elite, isLoaded, plan } = useSubscription();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  // Router location (not window.location): correct under any router, and what Back/Forward actually change.
  const locationRef = useRef(location);
  locationRef.current = location;
  const isDark = theme === "dark";

  // If user is on a tech page they haven't selected, redirect them to the selection page
  useEffect(() => {
    if (isLoaded && hasSelection && tech !== hasSelection) {
      navigate(`/elite-mastery/${hasSelection}`);
    }
  }, [isLoaded, hasSelection, tech, navigate]);
  const [showChat, setShowChat] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const chatBotRef = useRef(null);

  // Check if this is a tenant student (college flow) - they should have access
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const host = window.location.hostname;
  const isTenantHost = host !== 'theskillgenie.com' && 
                      host !== 'www.theskillgenie.com' && 
                      host !== 'localhost' &&
                      host !== '127.0.0.1';
  
  const isTenantStudent = (user && (user.role === 'student' || user.role === 'tenant-student' || user.role === 'admin') && (user.tenant || user.college_id || isTenantHost)) ||
                          (tenant && tenant.domain !== 'theskillgenie.com');

  // Determine user access level
  const hasPremiumAccess = isPaid || isTenantStudent || user.role === 'admin';
  const hasSelection = elite?.selection;

  // Final filtered list of technologies based on user's selection status
  const filteredTechnologies = technologies.filter(t => {
    // Strictly enforce selection: if they have a selection, only show that.
    // If they don't have a selection, they should be redirected or shown the modal.
    if (!hasSelection) return true; 
    return t.technology === hasSelection;
  });

  // Get user ID
  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user?.userId || user?.user_id || null;
    } catch {
      return null;
    }
  };

  // Load Elite Technologies. Was a hardcoded ["java-premium", "python-premium"]
  // pair — now sourced from the elite_technologies catalog (see
  // useEliteTechnologies.js) so this stays in sync with the picker and the
  // admin console without a code change.
  const { technologies: catalogTechs, loading: catalogLoading } = useEliteTechnologies();

  useEffect(() => {
    const loadTechMappings = async () => {
      const techs = [];
      const availableTechs = catalogTechs.filter((t) => !t.isComingSoon);
      for (const t of availableTechs) {
        const docsFolder = t.docsFolder || t.id;
        try {
          const res = await fetch(`/docs/${docsFolder}/mapping.json`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const mappingData = await res.json();
          techs.push(buildTechnologyTree({ id: t.id, title: t.title, docsFolder }, mappingData));
        } catch (err) {
          console.error(`Failed to load mapping for ${t.id}:`, err);
        }
      }

      setTechnologies(techs);
    };

    if (!catalogLoading && catalogTechs.length > 0) {
      loadTechMappings();
    }
  }, [catalogTechs, catalogLoading]);

  // Check if user needs to make a selection
  useEffect(() => {
    if (!isLoaded) return;

    if (!hasSelection) {
      setShowSelectionModal(true);
    } else if (tech !== hasSelection) {
      // If user tries to access a tech they haven't selected, redirect to their selected one
      // Requirement: "no access for other"
      navigate(`/elite-mastery/${hasSelection}`);
    }
  }, [isLoaded, hasSelection, tech]);

  // Load mapping when technology selected
  useEffect(() => {
    if (!tech) return;
    fetch(`/docs/${tech}/mapping.json`)
      .then((res) => res.json())
      .then(setMapping)
      .catch(() => setMapping(null));
  }, [tech]);

  // Load initial scroll position from URL
  useEffect(() => {
    const params = new URLSearchParams(locationRef.current.search);
    const scroll = params.get('scroll');
    if (scroll && isLoaded) {
        const container = document.getElementById('mastery-content-area');
        if (container) {
            setTimeout(() => {
                container.scrollTop = parseInt(scroll);
            }, 1000);
        }
    }
  }, [isLoaded, selected.subtopic]);

  // Track reading progress
  useEffect(() => {
    const container = document.getElementById('mastery-content-area');
    if (!container || !selected.subtopic) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
      setReadingProgress(progress);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selected.subtopic]);

  // Save progress periodically
  useEffect(() => {
    if (!selected.subtopic || !tech) return;

    const saveProgress = async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;

      try {
        await fetch(`${API_BASE_URL}/api/user/save-reading`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'user-id': userId
          },
          body: JSON.stringify({
            module: 'Elite Mastery',
            technology: tech,
            topic: selected.topic || 'General',
            subtopic: selected.subtopic,
            file_path: selected.subtopic,
            scroll_position: Math.round(readingProgress),
            reading_percentage: Math.round(readingProgress),
            time_spent: 30,
            status: readingProgress > 90 ? 'Completed' : 'In Progress'
          })
        });
        
        // Also update consistency
        const { updateLearningConsistency } = await import('../utils/consistency');
        updateLearningConsistency(userId);
      } catch (err) {
        console.error('Failed to save reading progress:', err);
      }
    };

    const interval = setInterval(saveProgress, 30000);
    return () => clearInterval(interval);
  }, [selected.subtopic, tech, readingProgress]);

  const handleSelectionSuccess = (selectedTech) => {
    navigate(`/elite-mastery/${selectedTech}`);
    window.location.reload();
  };

  // ── Topic sequence (single source of truth for position + Previous/Next) ──
  const sequences = useMemo(() => buildSequences(technologies), [technologies]);
  const located = useMemo(() => locateFile(sequences, selected.subtopic), [sequences, selected.subtopic]);
  const legacyFullAccess = hasPremiumAccess || isLegacyCollegeUser();
  const accessFor = useCallback((entry) => ({ locked: legacyIsLocked(entry.file, entry.index, legacyFullAccess), state: null }), [legacyFullAccess]);
  const nav = useMemo(() => (located ? resolveNavigation(located.sequence, selected.subtopic, accessFor) : null), [located, selected.subtopic, accessFor]);
  const completedInTechnology = useMemo(() => {
    if (!nav) return null;
    const done = completedFiles(nav.technology.id);
    return located.sequence.entries.filter((e) => done.has(e.file)).length;
  }, [nav, located]);

  const openTopic = useCallback((entry, opts = {}) => {
    if (!entry) return;
    const locked = typeof opts.locked === 'boolean' ? opts.locked : accessFor(entry).locked;
    if (locked) { setLockedFile(entry.file); setShowUpgradeModal(true); return; }
    setLockedFile(null);
    setSelected((prev) => ({ ...prev, topic: entry.sectionTitle, subtopic: entry.file }));
    const search = topicSearch(null, entry.key, locationRef.current.search);
    if (search !== locationRef.current.search) navigate({ pathname: locationRef.current.pathname, search }, { replace: !!opts.replace });
    if (!opts.keepScroll) {
      window.requestAnimationFrame(() => {
        const container = document.getElementById('mastery-content-area');
        if (container) container.scrollTo({ top: 0, behavior: 'instant' });
      });
    }
  }, [accessFor, navigate]);

  const handleFileSelect = (file, isLocked) => {
    const hit = locateFile(sequences, file);
    if (hit) { openTopic(hit.entry, { locked: isLocked }); return; }
    if (isLocked) {
      setLockedFile(file);
      setShowUpgradeModal(true);
    } else {
        setLockedFile(null);
        setSelected(prev => ({ ...prev, subtopic: file }));
    }
  };

  // Deep links: `?topic=<key>` (stable) and the Dashboard's `?file=<path>`;
  // Back/Forward re-resolve the topic from the URL.
  const urlState = useMemo(() => parseTopicSearch(location.search), [location.search]);
  const lastUrlTopic = useRef(urlState.topic);
  useEffect(() => {
    if (sequences.size === 0) return;
    const previousTopic = lastUrlTopic.current;
    lastUrlTopic.current = urlState.topic;
    let entry = null;
    if (urlState.topic) for (const seq of sequences.values()) { entry = seq.byKey.get(urlState.topic); if (entry) break; }
    if (!entry && urlState.file) { const hit = locateFile(sequences, urlState.file); entry = hit ? hit.entry : null; }
    if (entry) { if (entry.file !== selected.subtopic) openTopic(entry, { replace: true, keepScroll: true }); }
    else if (previousTopic && !urlState.topic && selected.subtopic) setSelected((prev) => ({ ...prev, subtopic: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlState.topic, urlState.file, sequences]);

  const leaveTopic = useCallback(() => {
    setSelected((prev) => ({ ...prev, subtopic: null }));
    const search = topicSearch(null, null, locationRef.current.search);
    if (search !== locationRef.current.search) navigate({ pathname: locationRef.current.pathname, search });
  }, [navigate]);

  return (
    <div className={`${isDark ? "bg-[#0B0F1A]" : "bg-[#F8FAFC]"} min-h-screen flex flex-col font-sans selection:bg-primary/30`}>
      {/* 🧭 Top Navigation Bar */}
      <header className={`h-16 flex items-center justify-between px-6 sticky top-0 z-[70] border-b backdrop-blur-md ${
        isDark ? "bg-[#0B0F1A]/80 border-white/5" : "bg-gradient-to-r from-blue-500 to-blue-600 border-blue-700"
      }`}>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              // Determine the correct dashboard path based on domain/tenant
              const isMainPlatform = !tenant || 
                                   tenant.domain === 'theskillgenie.com' || 
                                   tenant.tenantKey === 'theskillgenie' ||
                                   window.location.hostname === 'theskillgenie.com' ||
                                   window.location.hostname === 'www.theskillgenie.com' ||
                                   window.location.hostname === 'localhost';
              
              const userType = localStorage.getItem('userType');
              
              if (isMainPlatform) {
                if (userType === 'student') {
                  navigate('/dashboard');
                } else if (userType === 'college') {
                  navigate('/admin/dashboard');
                } else {
                  navigate('/dashboard');
                }
              } else {
                if (userType === 'student') {
                  navigate('/student/dashboard');
                } else if (userType === 'college') {
                  navigate('/admin/dashboard');
                } else {
                  navigate('/dashboard');
                }
              }
            }}
            className={`p-2 rounded-lg transition-colors ${isDark ? "hover:bg-white/10 text-white" : "hover:bg-white/20 text-white"}`}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-lg ${isDark ? "bg-blue-500 shadow-blue-500/20" : "bg-white shadow-black/10"}`}>
              <Sparkles className={isDark ? "text-white" : "text-blue-600"} size={18} />
            </div>
            <span className={`text-lg font-bold tracking-tight ${isDark ? "text-white" : "text-white"}`}>
              {tech ? tech.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Elite Technology Mastery'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={`p-2 rounded-lg transition-colors ${isDark ? "hover:bg-white/10 text-white" : "hover:bg-white/20 text-white"}`}
            title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
          >
            <BookOpen size={20} />
          </button>
          <ThemeToggle />
          {!hasPremiumAccess && (
             <button
                onClick={() => navigate('/skillgenpayments')}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-white text-blue-600 hover:bg-blue-50 transition-all shadow-lg shadow-black/10"
              >
                Upgrade to Premium
              </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {!isLoaded ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : !hasSelection ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="max-w-md space-y-6">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <Sparkles size={48} className="text-blue-600" />
              </div>
              <h2 className={`text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>Choose Your Path</h2>
              <p className={`${isDark ? "text-gray-400" : "text-gray-600"}`}>
                To access Elite Technology Mastery, you must first select the technology you wish to master. 
                Remember, you can only choose <span className="font-bold">one</span>.
              </p>
              <button 
                onClick={() => setShowSelectionModal(true)}
                className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
              >
                Select Technology Now
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Sidebar */}
            <div className={`transition-all duration-300 ease-in-out ${isSidebarOpen ? 'w-80' : 'w-0 opacity-0 pointer-events-none'} border-r overflow-y-auto custom-scrollbar ${isDark ? "bg-[#0B0F1A] border-white/5" : "bg-white border-gray-200"}`}>
              <SidebarTechTree
                data={filteredTechnologies}
                onSelectSubtopic={(file, isLocked) => handleFileSelect(file, isLocked)}
                theme={theme}
                selectedFile={selected.subtopic}
                isPaid={hasPremiumAccess}
              />
            </div>

            {/* Content Area */}
            <div id="mastery-content-area" className="flex-1 overflow-y-auto transition-all duration-300 ease-in-out">
              {!selected.subtopic ? (
                <div className={`h-full flex items-center justify-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  <div className="text-center space-y-4">
                    <div className={`mx-auto w-20 h-20 rounded-2xl flex items-center justify-center ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                      <Sparkles size={40} className="text-blue-500" />
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                        Select a topic to start learning
                      </h3>
                      <p className="text-sm mt-2">
                        Master {tech?.replace('-premium', '')} with our elite documentation
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-8 max-w-5xl mx-auto">
                  {nav && (
                    <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className={`text-xs font-bold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{nav.section.title}</span>
                      <span aria-hidden="true" className="text-gray-300">·</span>
                      <TopicPosition nav={nav} isDark={isDark} />
                    </div>
                  )}
                  <OnThisPageDropdown headings={headings} activeHeading={activeHeading} onSelect={(id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} isDark={isDark} className="mb-6" />
                  <EliteMarkdownViewer 
                    filePath={selected.subtopic} 
                    locked={lockedFile === selected.subtopic && !hasPremiumAccess}
                    onHeadingsCollected={setHeadings}
                    activeHeading={activeHeading}
                    setActiveHeading={setActiveHeading}
                    headings={headings}
                    readingProgress={readingProgress}
                    setReadingProgress={setReadingProgress}
                  />
                  <TopicNavigationBar
                    nav={nav}
                    isDark={isDark}
                    onGo={(entry, o) => openTopic(entry, { access: o && o.access })}
                    onBackToTechnology={leaveTopic}
                    completedCount={completedInTechnology}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ChatBot Integration */}
      {showChat && (
        <div ref={chatBotRef} className="fixed bottom-6 right-6 z-[100]">
          <ChatBot onClose={() => setShowChat(false)} />
        </div>
      )}

      {/* Selection Modal for Free Users (First Visit) */}
      <div className="relative z-[2000]">
        <EliteSelectionModal
          isOpen={showSelectionModal}
          onClose={() => {
            setShowSelectionModal(false);
            navigate('/dashboard');
          }}
          userId={getUserId()}
          onSelectionSuccess={handleSelectionSuccess}
        />
      </div>

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Unlock All Elite Technologies"
        message="Upgrade to premium to access all Elite Technology documentation without limits. View both Java Premium and Python Premium with full content access."
      />
    </div>
  );
};

export default EliteTechnologyDetail;
